import { useTranslation } from 'react-i18next';
import { formatSigned } from '../common/format';
import { InjuryBadge } from '../common/InjuryBadge';
import { SortHeader } from '../common/SortHeader';
import type { GameChip, RoundGoalieRow, RoundSkaterRow } from './todayViewModel';

interface RoundPlayersProps {
    skaters: RoundSkaterRow[];
    goalies: RoundGoalieRow[];
    skaterOrder: string;
    goalieOrder: string;
    onSkaterSort(column: string): void;
    onGoalieSort(column: string): void;
    // False when the league does not record skater time on ice (AHL).
    skaterToi?: boolean;
}

const Chip = ({ chip }: { chip?: GameChip }) =>
    chip === undefined ? null : <span className={`chip${chip.live ? ' live' : ''}`}>{chip.label}</span>;

// The round's stat lines of our players: one table for the skaters and one for
// the goalies, each sortable. Sorting is remembered between visits.
export function RoundPlayers({
    skaters, goalies, skaterOrder, goalieOrder, onSkaterSort, onGoalieSort, skaterToi = true,
}: RoundPlayersProps) {
    const { t } = useTranslation();

    if (skaters.length === 0 && goalies.length === 0) {
        return <p className="state-message">{t('today_noPlayersYet')}</p>;
    }

    return (
        <div className="round-players">
            {skaters.length > 0 &&
                <table className="dt" data-testid="round-skaters">
                    <thead>
                        <tr>
                            <SortHeader column="name" order={skaterOrder} onSort={onSkaterSort} align="left">{t('col_player')}</SortHeader>
                            <th scope="col" className="left">{t('col_game')}</th>
                            <SortHeader column="pointsLatest" order={skaterOrder} onSort={onSkaterSort} title={t('scFull_points')}>{t('col_points')}</SortHeader>
                            <SortHeader column="shotsLatest" order={skaterOrder} onSort={onSkaterSort} title={t('scFull_shots')}>{t('col_shots')}</SortHeader>
                            <SortHeader column="plusMinusLatest" order={skaterOrder} onSort={onSkaterSort} title={t('scFull_plusMinus')}>{t('col_plusMinus')}</SortHeader>
                            {skaterToi &&
                                <SortHeader column="toiLatest" order={skaterOrder} onSort={onSkaterSort} title={t('scFull_toi')}>{t('col_toi')}</SortHeader>}
                        </tr>
                    </thead>
                    <tbody>
                        {skaters.map(row => (
                            <tr key={row.player.playerId}>
                                <td className="left name"><span className="hl" title={row.player.displayName}>{row.player.lastName}</span><InjuryBadge level={row.player.injury} /></td>
                                <td className="left"><Chip chip={row.chip} /></td>
                                <td className="strong">{row.goals}+{row.assists}</td>
                                <td>{row.shots}</td>
                                <td>{formatSigned(row.plusMinus)}</td>
                                {skaterToi && <td>{row.timeOnIce}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>}
            {goalies.length > 0 &&
                <>
                    <h3 className="section-head">{t('today_goalies')}</h3>
                    <table className="dt" data-testid="round-goalies">
                        <thead>
                            <tr>
                                <SortHeader column="name" order={goalieOrder} onSort={onGoalieSort} align="left">{t('col_player')}</SortHeader>
                                <th scope="col" className="left">{t('col_game')}</th>
                                <SortHeader column="savePctLatest" order={goalieOrder} onSort={onGoalieSort} title={t('scFull_savePct')}>{t('col_savePct')}</SortHeader>
                                <SortHeader column="savesLatest" order={goalieOrder} onSort={onGoalieSort} title={t('scFull_saves')}>{t('col_saves')}</SortHeader>
                                <SortHeader column="toiLatest" order={goalieOrder} onSort={onGoalieSort} title={t('scFull_toi')}>{t('col_toi')}</SortHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {goalies.map(row => (
                                <tr key={row.player.playerId}>
                                    <td className="left name"><span className="hl" title={row.player.displayName}>{row.player.lastName}</span><InjuryBadge level={row.player.injury} /></td>
                                    <td className="left"><Chip chip={row.chip} /></td>
                                    <td className="strong">{(row.savePct * 100).toFixed(1)}</td>
                                    <td>{row.saves}/{row.shotsAgainst}</td>
                                    <td>{row.timeOnIce}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>}
        </div>
    );
}
